import React, { useEffect, useState } from 'react';
import { FileText, Download, Plus, RefreshCw } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '../../../components/ui/combobox';
import StatusChip from '../../../components/common/StatusChip';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import { mamReportService } from '../services/mamService';
import { formatDate } from '../utils';
import type { MAMReport } from '../types';

export default function MAMReports() {
  const [reports, setReports] = useState<MAMReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const [reportType, setReportType] = useState('performance');
  const [format, setFormat] = useState('PDF');
  const [period, setPeriod] = useState('July 2026');

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mamReportService.getAll();
      setReports(data);
    } catch {
      setError('Failed to fetch reports list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setGenerating(true);
      const newReport = await mamReportService.generate(
        reportType,
        format,
        period
      );
      setReports(prev => [newReport, ...prev]);
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = (report: MAMReport) => {
    const dummyContent = `Report Title: ${report.title}\nFormat: ${report.format}\nPeriod: ${report.period}\nGenerated By: ${report.generatedBy}`;
    const blob = new Blob([dummyContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.title.toLowerCase().replace(/\s+/g, '_')}.${report.format.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: DataTableColumn<MAMReport>[] = [
    {
      id: 'title',
      header: 'Report Title',
      sortable: true,
      accessor: r => (
        <div className="flex items-center space-x-2">
          <FileText className="w-4 h-4 text-indigo-600" />
          <div>
            <p className="font-bold text-foreground">{r.title}</p>
            <p className="text-[10px] text-muted-foreground">
              Generated by: {r.generatedBy}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'format',
      header: 'Format',
      sortable: true,
      align: 'center',
      accessor: r => (
        <span className="font-mono font-bold text-xs px-2 py-0.5 bg-muted rounded">
          {r.format}
        </span>
      ),
    },
    {
      id: 'period',
      header: 'Period',
      sortable: true,
      accessor: r => <span className="text-xs">{r.period}</span>,
    },
    {
      id: 'fileSize',
      header: 'File Size',
      sortable: true,
      align: 'right',
      accessor: r => (
        <span className="text-xs text-muted-foreground">{r.fileSize}</span>
      ),
    },
    {
      id: 'generatedAt',
      header: 'Date Created',
      sortable: true,
      accessor: r => (
        <span className="text-[10px] text-muted-foreground">
          {formatDate(r.generatedAt)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: r => <StatusChip status={r.status} size="sm" />,
    },
    {
      id: 'actions',
      header: 'Download',
      align: 'right',
      accessor: r => (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => downloadReport(r)}
        >
          <Download className="w-3.5 h-3.5 mr-1" /> Download
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Reports" />
        <LoadingSkeleton type="table" rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Reports" />
        <ErrorState message={error} onRetry={loadReports} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Reports & Export Center"
        description="Generate audit logs, client statements, trade history & commission reports"
      >
        <Button variant="outline" size="sm" onClick={loadReports}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      {/* Generator Form */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Generate New Custom Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleGenerate}
            className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs items-end"
          >
            <div>
              <label className="block font-medium text-foreground mb-1">
                Report Type
              </label>
              <Combobox
                items={[
                  'Performance & ROI',
                  'Master/Child Trade Audit',
                  'Client Account Statements',
                  'Commission Summary',
                ]}
                value={
                  reportType === 'performance'
                    ? 'Performance & ROI'
                    : reportType === 'trades'
                      ? 'Master/Child Trade Audit'
                      : reportType === 'client_statement'
                        ? 'Client Account Statements'
                        : 'Commission Summary'
                }
                onValueChange={val => {
                  const map: Record<string, string> = {
                    'Performance & ROI': 'performance',
                    'Master/Child Trade Audit': 'trades',
                    'Client Account Statements': 'client_statement',
                    'Commission Summary': 'commission',
                  };
                  setReportType(map[val] ?? 'performance');
                }}
              >
                <ComboboxInput
                  className="h-8 text-xs"
                  placeholder="Select report type..."
                />
                <ComboboxContent>
                  <ComboboxEmpty>No types found</ComboboxEmpty>
                  <ComboboxList
                    renderItem={item => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  />
                </ComboboxContent>
              </Combobox>
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                File Format
              </label>
              <Combobox
                items={['PDF Document', 'CSV Spreadsheet', 'Excel (.xlsx)']}
                value={
                  format === 'PDF'
                    ? 'PDF Document'
                    : format === 'CSV'
                      ? 'CSV Spreadsheet'
                      : 'Excel (.xlsx)'
                }
                onValueChange={val => {
                  const map: Record<string, string> = {
                    'PDF Document': 'PDF',
                    'CSV Spreadsheet': 'CSV',
                    'Excel (.xlsx)': 'XLSX',
                  };
                  setFormat(map[val] ?? 'PDF');
                }}
              >
                <ComboboxInput
                  className="h-8 text-xs font-mono"
                  placeholder="Select format..."
                />
                <ComboboxContent>
                  <ComboboxEmpty>No formats found</ComboboxEmpty>
                  <ComboboxList
                    renderItem={item => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  />
                </ComboboxContent>
              </Combobox>
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1">
                Time Period
              </label>
              <Combobox
                items={['July 2026 (Current)', '2026 Q2', '2026 YTD']}
                value={period === 'July 2026' ? 'July 2026 (Current)' : period}
                onValueChange={val => {
                  const map: Record<string, string> = {
                    'July 2026 (Current)': 'July 2026',
                    '2026 Q2': '2026 Q2',
                    '2026 YTD': '2026 YTD',
                  };
                  setPeriod(map[val] ?? 'July 2026');
                }}
              >
                <ComboboxInput
                  className="h-8 text-xs"
                  placeholder="Select period..."
                />
                <ComboboxContent>
                  <ComboboxEmpty>No periods found</ComboboxEmpty>
                  <ComboboxList
                    renderItem={item => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  />
                </ComboboxContent>
              </Combobox>
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        data={reports}
        searchKeys={['title', 'generatedBy', 'format', 'period']}
        searchPlaceholder="Search reports..."
        initialPageSize={6}
      />
    </div>
  );
}
